import React from 'react';
import { FaTimes, FaReceipt } from 'react-icons/fa';

const TaxViewModal = ({ open, onClose, tax }) => {
  if (!open || !tax) return null;

  const getStatusColor = (status) => {
    // Handle boolean, string, and numeric status values
    const isActive = status === true || status === 'Active' || status === 1 || status === '1';
    return isActive ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500';
  };

  const getStatusText = (status) => {
    // Convert boolean, numeric or string status to text
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };

  const getDefaultText = (isDefault) => {
    // Convert boolean, numeric or string IsDefault to text
    if (isDefault === true || isDefault === 1 || isDefault === '1') return 'Yes';
    if (isDefault === false || isDefault === 0 || isDefault === '0') return 'No';
    return isDefault ? 'Yes' : 'No';
  };

  const getDefaultColor = (isDefault) => {
    // Handle boolean, string, and numeric IsDefault values
    const isDefaultValue = isDefault === true || isDefault === 1 || isDefault === '1';
    return isDefaultValue ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500';
  };

  const sections = [
    {
      title: 'Tax Information',
      fields: [
        { label: 'Tax Code', value: tax.TaxCode },
        { label: 'Tax Type', value: tax.TaxType },
        { label: 'Tax Name', value: tax.TaxName },
        { label: 'Rate', value: `${tax.Rate}%` },
        { label: 'Country', value: tax.Country || 'N/A' },
        { label: 'Default Tax', value: tax.IsDefault, type: 'isDefault' },
        { label: 'Status', value: tax.Status, type: 'status' },
      ]
    },
    tax.Description ? {
      title: 'Description',
      fields: [
        { label: 'Details', value: tax.Description },
      ]
    } : null,
    {
      title: 'System Information',
      fields: [
        { label: 'Created By', value: tax.CreatedBy || 'N/A' },
      ]
    }
  ].filter(Boolean); // Filter out null sections (e.g., if description is empty)

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-2xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
      {/* Close Button */}
      <button
        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-[#2b6777] hover:text-[#52ab98]"
        onClick={onClose}
      >
        <FaTimes size={18} className="sm:w-5 sm:h-5" />
      </button>

      {/* Title */}
      <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        Tax Details
      </h2>

      <div className="space-y-4 sm:space-y-6">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="bg-white rounded-xl p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3 sm:mb-4">
              {section.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex flex-col">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">
                    {field.label}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 mt-1">
                    {field.type === "status" ? (
                      <span
                        className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(
                          field.value
                        )}`}
                      >
                        {getStatusText(field.value)}
                      </span>
                    ) : field.type === "isDefault" ? (
                      <span
                        className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold shadow-sm ${getDefaultColor(
                          field.value
                        )}`}
                      >
                        {getDefaultText(field.value)}
                      </span>
                    ) : (
                      field.value || "-"
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Tax Summary */}
        <div className="bg-white rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-[#2b6777] mb-2 sm:mb-3">
            Tax Summary
          </h3>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">
            This tax is configured as <strong>{tax.TaxType}</strong> with a rate of{" "}
            <strong>{tax.Rate}%</strong>. The tax is currently{" "}
            <strong>{getStatusText(tax.Status)}</strong> and is{" "}
            {getDefaultText(tax.IsDefault) === "Yes"
              ? "set as the default tax"
              : "not set as the default tax"}
            .
            {tax.Country && ` It applies to ${tax.Country}.`}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 sm:mt-6 flex justify-end">
        <button
          className="px-3 sm:px-4 py-1.5 sm:py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white text-sm sm:text-base font-semibold shadow transition"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

};

export default TaxViewModal; 